/**
 * js/client-intake-view.js — read what a client sent back, without applying it.
 *
 * Until this module there was exactly one consumer of the answers an intake
 * link brings home: ClientIntakeLink.apply(). That path needs the matching
 * draft open, runs the incoming-fill clobber guard, and ends in a write. So
 * "what did the client actually send?" could only be answered by starting to
 * commit it, which is the wrong shape for triage and impossible from a tool
 * that is not the quoter.
 *
 * EAGER (index.html, precached in sw.js), and that is the whole design. Three
 * surfaces want to read a submission — the Submissions panel (lazy, inside the
 * quoting/commercial bundle), the Client Portal tool (its own tool, its own
 * scripts array) and the dashboard card (eager). Putting the viewer inside
 * js/client-intake-link.js would make the Client Portal's first row click pull
 * ~149 KB of quoter bundle down to render a read-only table. Eager serving
 * lazy is the direction that works; every consumer reads window.ClientIntakeView
 * at CALL time and degrades to nothing if it is absent.
 *
 * It writes NOTHING. No apply, no dismiss, no local link state, no ActivityLog
 * import event. The only network call is POST {action:'get', id}, which
 * api/intake-links.js already scopes to the sender or an agency admin.
 *
 * SECURITY: every answer value on this screen is client-typed free text that
 * travelled through the website and an app-server-encrypted table. It is the
 * most untrusted string in this feature and this module is what puts it on
 * screen, so every value, label, group title and option label goes through
 * esc() — whose fallback actually escapes rather than coercing with String().
 *
 * It also owns STATUS_LABEL, the one link-status vocabulary, because
 * js/portal-admin.js now needs the same words and a second copy of a label
 * table is the drift CLAUDE.md keeps paying for. js/client-intake-link.js
 * reads statusLabel() back from here at call time (the reminders-utils.js
 * precedent).
 */
window.ClientIntakeView = (function () {
    'use strict';

    const ENDPOINT = '/api/intake-links';
    const MODAL_ID = 'civView';

    // The one link-status vocabulary. js/client-intake-link.js forwards to
    // statusLabel() below; js/portal-admin.js reads it for its forms card.
    const STATUS_LABEL = Object.freeze({
        created: 'Sending…',
        sent: 'Sent',
        opened: 'Opened by the client',
        locked: 'Locked (too many code attempts)',
        submitted: 'Submitted',
        applied: 'Applied',
        dismissed: 'Dismissed',
        revoked: 'Revoked',
        send_failed: 'Send failed',
        expired: 'Expired',
    });

    // Only these two carry answers — api/intake-links.js handleGet returns
    // `answers: null` for everything else, so there is nothing to render.
    const ANSWERED = Object.freeze(['submitted', 'applied']);
    // A link the client can still work on. api/intake-links.js handleGet
    // returns what they have saved so far as `draft` for exactly these, so the
    // agent can watch the form fill in (and, before the first save, read what
    // the client was asked).
    const PROGRESS = Object.freeze(['sent', 'opened']);
    // How often an open progress view asks again. The client saves on each
    // Next, so nothing changes faster than a page at a time.
    const PROGRESS_POLL_MS = 30000;
    const LIVE = Object.freeze(['created', 'sent', 'opened', 'locked']);
    const KIND_TAG = Object.freeze({ personal: 'PL', commercial: 'COMM' });
    const REPEATABLES = Object.freeze(['drivers', 'vehicles']);
    const ROW_NOUN = Object.freeze({ drivers: 'Driver', vehicles: 'Vehicle' });
    // Which of a row's own answers make a heading a person recognises.
    const ROW_TITLE_FIELDS = Object.freeze({
        drivers: Object.freeze(['firstName', 'lastName']),
        vehicles: Object.freeze(['year', 'make', 'model']),
    });

    let _openId = '';
    let _pollTimer = null;

    // ── helpers ────────────────────────────────────────────────────────

    const _fallbackEsc = (s) => String(s == null ? '' : s)
        .replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const esc = (s) => (window.Utils && window.Utils.escapeHTML ? window.Utils.escapeHTML(String(s == null ? '' : s)) : _fallbackEsc(s));
    const isPlain = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
    const has = (o, k) => Object.prototype.hasOwnProperty.call(o || {}, k);
    // `false` is a real toggle answer. Only undefined / null / '' are blank.
    const isBlank = (v) => v === undefined || v === null || v === '';
    const quiet = (t) => `<span class="civ-quiet">${esc(t)}</span>`;

    function statusLabel(status) {
        const s = String(status || '');
        if (has(STATUS_LABEL, s)) return STATUS_LABEL[s];
        return s.replace(/_/g, ' ') || 'Unknown';
    }

    function chipClass(status) {
        const s = String(status || '');
        if (s === 'submitted') return 'cil-chip-submitted';
        if (s === 'applied') return 'cil-chip-applied';
        return LIVE.includes(s) ? 'cil-chip-live' : 'cil-chip-dead';
    }

    function fmtDate(iso) {
        if (!iso) return '';
        // The agency's calendar day (Pacific): a submission that landed at
        // 8 pm reads as that evening's date wherever the agent is sitting.
        // The viewer's own zone is only the fallback.
        if (window.Utils && typeof Utils.formatAgencyDate === 'function') {
            const s = Utils.formatAgencyDate(iso);
            if (s) return s;
        }
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
        try { return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch (_) { return d.toISOString().slice(0, 10); }
    }

    /**
     * One answer as the agent should read it.
     *
     * A toggle is strictly boolean or '' on the wire (api/_intake-spec.js
     * validateValue), so no string coaxing is needed. A select carries a value,
     * not a label, and a dependsOn cascade ships `options: []` with its real
     * labels under optionGroups — reading only `options` is how a cascaded
     * answer renders as a raw code. A date stays VERBATIM: these are bare
     * YYYY-MM-DD strings and toLocaleDateString parses those as UTC, which
     * reports a birthday a day early west of Greenwich.
     */
    /**
     * The label a row reference is read under: the named fields of the row
     * it points at ("Sam Sample"), else "<fallback> <n>". The ONE resolver
     * for `#<index>` values: the read-only view, the portal card, the review
     * modal and the merge (js/client-intake-spec.js reads it at call time)
     * all go through it, so no surface prints "#1". `bags[index]` is a plain
     * bag of that row's effective values, whatever the caller built it from
     * (answers over prefill here, the live roster on the merge side).
     */
    function rowRefLabel(optionsFrom, index, bags) {
        const of = isPlain(optionsFrom) ? optionsFrom : {};
        const i = Number(index);
        const bag = Array.isArray(bags) && Number.isInteger(i) && i >= 0 ? bags[i] : null;
        const parts = (Array.isArray(of.labelFields) ? of.labelFields : [])
            .map((fid) => (isPlain(bag) && has(bag, fid) && !isBlank(bag[fid]) ? String(bag[fid]).trim() : ''))
            .filter(Boolean);
        if (parts.length) return parts.join(' ');
        return `${of.fallback || 'Row'} ${Number.isInteger(i) ? i + 1 : ''}`.trim();
    }

    const ROW_REF_RE = /^#(0|[1-9]\d*)$/;

    /** ***-**-6789: an SSN by its last four, the one way any screen shows it. */
    function maskSsn(v) {
        const d = String(v == null ? '' : v).replace(/\D/g, '');
        return d.length >= 4 ? `***-**-${d.slice(-4)}` : (d ? 'Entered' : '');
    }

    function show(field, v, ctx) {
        const f = field || {};
        if (typeof v === 'boolean') return v ? 'Yes' : 'No';
        const w = String(f.widget || '');
        if (w === 'select' && isPlain(f.optionsFrom)) {
            // A reference into another repeatable's rows: never the raw `#1`.
            const m = ROW_REF_RE.exec(String(v));
            const bags = ctx && isPlain(ctx.refs) ? ctx.refs[f.optionsFrom.repeatable] : null;
            if (m) return rowRefLabel(f.optionsFrom, Number(m[1]), bags);
            return String(v);
        }
        if (w === 'select') {
            const pools = [];
            if (Array.isArray(f.options)) pools.push(f.options);
            if (isPlain(f.optionGroups)) {
                Object.keys(f.optionGroups).forEach((k) => {
                    if (Array.isArray(f.optionGroups[k])) pools.push(f.optionGroups[k]);
                });
            }
            for (const pool of pools) {
                const hit = pool.find((o) => o && o.value === v);
                if (hit && hit.label) return String(hit.label);
            }
            return String(v);
        }
        if (w === 'ssn') {
            // The owner's SSN (asked for a bond) reads by its last four here,
            // on the dashboard and on Client Portal alike. The full number goes
            // into the Commercial Intake on apply, behind that form's own 👁.
            return maskSsn(v);
        }
        if (w === 'naics') {
            // "238160 Roofing Contractors (primary); ..." when the code list
            // has loaded (it rides the Commercial Intake), else the codes.
            const N = window.NaicsSearch;
            if (N && typeof N.describeList === 'function') {
                try { const line = N.describeList(String(v)); if (line) return line; } catch (_) { /* codes below */ }
            }
            return String(v).split(',').join(', ');
        }
        if (w === 'money') {
            // A submitted money value arrives stripped of $ and commas, but a
            // PREFILL is lenient and may still carry them, so the guard is
            // real. "It's a number" is not a reason to skip Number.isFinite.
            const n = Number(String(v).replace(/[$,\s]/g, ''));
            return Number.isFinite(n) ? `$${n.toLocaleString()}` : String(v);
        }
        return String(v);
    }

    /**
     * The Answer cell for one field.
     *
     * `field.sensitive` does NOT mean "never asked" — it means never PREFILLED
     * (js/client-intake-spec.js:812). The client can and does answer dob and
     * dlNum, so a sensitive field with an answer renders that answer. What
     * says we already hold the value is `field.onFile`, an object carrying a
     * hint ("On file (ends 4821)"), or for a repeatable the row's own onFile
     * list — which is per ROW, because the field definition is shared by every
     * row and one driver's licence being on file says nothing about another's.
     */
    function answerCell(field, aBag, pBag, edited, rowOnFile, ctx) {
        const id = field && field.id;
        // On a form still being filled in, a blank is a page the client has
        // not reached, not a question they skipped.
        const blank = (ctx && ctx.progress) ? 'Not reached yet' : 'Not answered';
        if (!id) return quiet(blank);
        if (has(aBag, id) && !isBlank(aBag[id])) {
            const txt = esc(show(field, aBag[id], ctx));
            return (edited && edited.has(id)) ? `<span class="civ-edited">${txt}</span>` : txt;
        }
        if (rowOnFile && rowOnFile.has(id)) {
            return quiet((isPlain(field.onFile) && field.onFile.hint) || 'On file');
        }
        if (isPlain(field.onFile) && field.onFile.hint) return quiet(field.onFile.hint);
        if (has(pBag, id) && !isBlank(pBag[id])) {
            return `${esc(show(field, pBag[id], ctx))} ${quiet('from your file')}`;
        }
        return quiet(blank);
    }

    function fieldRows(fields, aBag, pBag, edited, rowOnFile, ctx) {
        return fields.map((f) => `<tr><th scope="row">${esc((f && f.label) || (f && f.id) || '')}</th>`
            + `<td>${answerCell(f, aBag, pBag, edited, rowOnFile, ctx)}</td></tr>`).join('');
    }

    function groupSection(title, rows) {
        if (!rows) return '';
        return `<section class="civ-group"><h5>${esc(title)}</h5>`
            + `<table class="cil-table civ-table"><tbody>${rows}</tbody></table></section>`;
    }

    function note(text) { return `<p class="cil-empty">${esc(text)}</p>`; }

    /**
     * One line saying where a client is with a form they have not submitted,
     * for the progress view's header and the Submissions / Client Forms rows.
     *
     * Reads the link summary only: `status` ('sent' | 'opened'), `openedAt`
     * (first open), `lastOpenedAt` (most recent open), `saveCount` (each Next
     * or Save for later), `sentAt`. Times are server instants; show them on
     * the agency's clock with Utils.formatAgencyDateTime(iso), or as days with
     * Utils.agencyDayDiff(iso) (0 = today). Read Utils defensively, the way
     * fmtDate() above does. Return '' to show nothing.
     */
    function progressLine(link) {
        // TODO(Austin): the wording of this line.
        return '';
    }

    /**
     * How long the client was actively on the form: api/intake-links.js
     * `activeSeconds` (migration 0075), the seconds the website counted while
     * the form was on screen and the client was typing, clicking or scrolling,
     * summed over every save and the submit. For the agency only: the client
     * never sees it. '' when nothing was measured, which is every form a
     * client answered before the website counted.
     */
    function durationText(secs) {
        if (secs < 60) return 'Under a minute';
        const mins = Math.round(secs / 60);
        if (mins < 60) return `About ${mins} min`;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `About ${h} hr${m ? ` ${m} min` : ''}`;
    }

    function timeLine(link) {
        const secs = Number(link && link.activeSeconds);
        if (!Number.isFinite(secs) || secs <= 0) return '';
        const soFar = PROGRESS.includes(String((link && link.status) || '')) ? ' so far' : '';
        return `${durationText(secs)} on the form${soFar}`;
    }

    /**
     * Who sent a form, short: their HawkSoft initials when the server found
     * them, else the name part of their email ("kathleen@…" reads "Kathleen").
     * The one wording the panel, the dashboard card and Client Portal share.
     */
    function senderLabel(link) {
        if (!link) return '';
        const ini = String(link.createdByInitials || '').trim();
        if (ini) return ini;
        const local = String(link.createdByEmail || '').split('@')[0].split(/[._+-]/)[0];
        return local ? local.charAt(0).toUpperCase() + local.slice(1).toLowerCase() : '';
    }

    function headHtml(link) {
        // Number(null) is 0 and Number('') is 0, so coercing first would print
        // "0 answers" for a row whose count api/intake-links.js deliberately
        // returns as null. A literal zero is indistinguishable from a measured
        // one, so an unknown count says nothing at all.
        const raw = link.answerCount;
        const n = isBlank(raw) ? NaN : Number(raw);
        const count = Number.isFinite(n) ? `${n} answer${n === 1 ? '' : 's'}` : '';
        const bits = [`<span class="cil-chip ${chipClass(link.status)}">${esc(statusLabel(link.status))}</span>`];
        if (link.submittedAt) bits.push(quiet(`Submitted ${fmtDate(link.submittedAt)}`));
        if (count) bits.push(quiet(count));
        const spent = timeLine(link);
        if (spent) bits.push(quiet(spent));
        if (PROGRESS.includes(String(link.status || ''))) {
            const line = progressLine(link);
            if (line) bits.push(quiet(line));
        }
        if (link.mine === false && senderLabel(link)) bits.push(quiet(`sent by ${senderLabel(link)}`));
        // Where the form went: the client is not asked again for an email the
        // agent typed to send it, so this is how the answers say how to reach them.
        const contact = [link.recipientEmail, link.recipientPhone].filter((v) => !isBlank(v)).map(String);
        if (contact.length) bits.push(quiet(`Sent to ${contact.join(' · ')}`));
        const tag = KIND_TAG[String(link.kind || '')] || '';
        return `<div class="civ-head">
            <strong class="civ-who">${esc(link.clientName || link.recipientMasked || 'Client')}</strong>
            ${tag ? `<span class="civ-tag">${esc(tag)}</span>` : ''}
            ${bits.join('')}
        </div>`;
    }

    /**
     * Row heading from the row's OWN values, so an agent reading three driver
     * blocks can tell them apart. Falls back to "Driver 2" rather than
     * inventing one.
     */
    function rowHeading(name, index, bag, pre, added) {
        if (added) return `New ${ROW_NOUN[name].toLowerCase()}`;
        const parts = (ROW_TITLE_FIELDS[name] || [])
            .map((fid) => (has(bag, fid) && !isBlank(bag[fid]) ? bag[fid] : pre[fid]))
            .filter((v) => !isBlank(v))
            .map((v) => String(v));
        return parts.length ? parts.join(' ') : `${ROW_NOUN[name]} ${index + 1}`;
    }

    /**
     * One row's effective values, answers over prefill, for every answer row
     * of a repeatable: what a reference from another repeatable resolves
     * against (a kept agent row's name is usually in the prefill, not retyped).
     */
    function effectiveRows(name, answers, prefill) {
        const rows = Array.isArray(answers && answers[name]) ? answers[name] : [];
        const preRows = (prefill && Array.isArray(prefill[name])) ? prefill[name] : [];
        const preById = new Map(preRows.map((r) => [r && r.id, isPlain(r && r.fields) ? r.fields : {}]));
        return rows.map((row) => Object.assign({}, (row && row.id !== null && preById.get(row.id)) || {}, isPlain(row && row.fields) ? row.fields : {}));
    }

    function repeatableSections(name, spec, answers, prefill, ctx) {
        const rep = spec.repeatables && spec.repeatables[name];
        if (!isPlain(rep) || !Array.isArray(rep.fields)) return '';
        const rows = Array.isArray(answers[name]) ? answers[name] : [];
        if (!rows.length) return '';
        const specRows = Array.isArray(rep.rows) ? rep.rows : [];
        const onFileById = new Map(specRows.map((r) => [r.id, new Set(Array.isArray(r.onFile) ? r.onFile : [])]));
        // Locked row fields are answered at the top of the form, not on the
        // row: the applicant's own card does not repeat them.
        const lockedById = new Map(specRows.map((r) => [r.id, new Set(Array.isArray(r.locked) ? r.locked : [])]));
        const known = new Set(specRows.map((r) => r.id));
        const preRows = (prefill && Array.isArray(prefill[name])) ? prefill[name] : [];
        const preById = new Map(preRows.map((r) => [r && r.id, isPlain(r && r.fields) ? r.fields : {}]));
        return rows.map((row, i) => {
            const bag = isPlain(row && row.fields) ? row.fields : {};
            // id null, or an id the spec never listed, is a row the CLIENT
            // added. Saying so is exactly what an agent wants to know before
            // they apply anything.
            const added = !row || row.id === null || !known.has(row.id);
            const pre = added ? {} : (preById.get(row.id) || {});
            const onFile = added ? null : onFileById.get(row.id);
            const locked = added ? null : lockedById.get(row.id);
            const fields = locked && locked.size ? rep.fields.filter((f) => !(f && locked.has(f.id))) : rep.fields;
            return groupSection(rowHeading(name, i, bag, pre, added), fieldRows(fields, bag, pre, null, onFile, ctx));
        }).join('');
    }

    /** "5 of 12 questions answered so far." over the top-level questions. */
    function answeredOf(spec, aBag) {
        const ids = Object.keys(spec.fields);
        const done = ids.filter((id) => has(aBag, id) && !isBlank(aBag[id])).length;
        return `${done} of ${ids.length} question${ids.length === 1 ? '' : 's'} answered so far.`;
    }

    /**
     * The whole modal body for one fetched link. PURE: no DOM reads, no
     * network, no module state. Every test drives this rather than the modal.
     */
    function renderBody(link) {
        if (!isPlain(link)) return note('That form could not be opened.');
        const status = String(link.status || '');
        const progress = PROGRESS.includes(status);
        if (!ANSWERED.includes(status) && !progress) {
            return headHtml(link) + note(status === 'revoked'
                ? 'This form was revoked before the client answered it.'
                : 'This form has not been submitted yet.');
        }
        // In progress, the client's saved draft stands in for the answers; with
        // no save yet it is empty and every question reads "Not reached yet",
        // which is the list of what they were asked.
        const answers = progress ? (isPlain(link.draft) ? link.draft : {}) : link.answers;
        if (!isPlain(answers)) {
            // Real and reachable: api/intake-links-sweep.js purges the payload
            // a couple of days after apply. An empty table would read as "the
            // client sent nothing", which is the opposite of what happened.
            return headHtml(link) + note(status === 'applied'
                ? `Applied ${fmtDate(link.appliedAt)}. The answers have been purged.`
                : 'The answers are no longer available.');
        }
        const spec = link.spec;
        if (!isPlain(spec) || !Array.isArray(spec.groups) || !isPlain(spec.fields)) {
            return headHtml(link) + note(progress
                ? 'This form cannot be shown: the question list it was built from is missing.'
                : 'This submission cannot be shown: the question list it was built from is missing.');
        }
        const ctx = { progress };
        const aBag = isPlain(answers.fields) ? answers.fields : {};
        const pBag = (isPlain(link.prefill) && isPlain(link.prefill.fields)) ? link.prefill.fields : {};
        // What the CLIENT changed. answers.fields carries the effective value
        // of every visible field, our own prefill echoed back included, which
        // is why answerCount routinely exceeds what they typed. This is the
        // list worth marking.
        const edited = new Set(Array.isArray(answers.edited) ? answers.edited : []);

        const groups = spec.groups.map((g) => {
            if (!isPlain(g) || !Array.isArray(g.fields)) return '';
            const defs = g.fields.map((fid) => (has(spec.fields, fid)
                ? Object.assign({ id: fid }, spec.fields[fid])
                : null)).filter(Boolean);
            return groupSection(g.title || '', fieldRows(defs, aBag, pBag, edited, null, ctx));
        }).join('');

        // Bags every row reference resolves against, per repeatable: the
        // client's answers over our prefill, in answer-row order (the order
        // a `#<index>` counts in).
        const refs = {};
        if (isPlain(spec.repeatables)) REPEATABLES.forEach((n) => { refs[n] = effectiveRows(n, answers, link.prefill); });
        const rowBlocks = isPlain(spec.repeatables)
            ? REPEATABLES.map((n) => repeatableSections(n, spec, answers, link.prefill, { refs, progress })).join('')
            : '';
        const lead = progress ? note(isPlain(link.draft)
            ? `Not submitted yet. ${answeredOf(spec, aBag)} This updates on its own while it stays open.`
            : 'The client has not saved any answers yet. These are the questions they were asked. This updates on its own while it stays open.') : '';

        return headHtml(link) + lead
            + `<div class="civ-body">${groups}${rowBlocks}</div>`
            + (edited.size ? `<p class="civ-legend">${quiet('Highlighted answers are what the client changed. Everything else is what we sent them.')}</p>` : '');
    }

    // ── modal ──────────────────────────────────────────────────────────

    function stopPoll() {
        if (_pollTimer) clearInterval(_pollTimer);
        _pollTimer = null;
    }

    function close() {
        stopPoll();
        const el = document.getElementById(MODAL_ID);
        if (el) el.remove();
        _openId = '';
    }

    function shell(bodyHtml, opts) {
        close();
        const back = (opts && isPlain(opts.onBack) && opts.onBack.label)
            ? `<button type="button" class="ch-btn" data-civ="back">${esc(opts.onBack.label)}</button>`
            : '';
        const overlay = document.createElement('div');
        overlay.className = 'ch-modal-overlay cil-overlay';
        overlay.id = MODAL_ID;
        overlay.innerHTML = `
            <div class="ch-modal cil-modal ch-modal-wide" role="dialog" aria-modal="true" aria-labelledby="civViewTitle">
                <div class="ch-modal-head">
                    <h4 id="civViewTitle">Client form</h4>
                    <button type="button" class="ch-modal-x" data-civ="close" aria-label="Close">✕</button>
                </div>
                <div class="cil-table-wrap civ-wrap" id="civViewBody">${bodyHtml}</div>
                <div class="ch-modal-actions">${back}<button type="button" class="ch-btn" data-civ="close">Close</button><span id="civApplySlot"></span></div>
            </div>`;
        const onKey = (e) => { if (e.key === 'Escape') { document.removeEventListener('keydown', onKey); close(); } };
        document.addEventListener('keydown', onKey);
        overlay.addEventListener('click', (e) => {
            const hit = e.target.closest ? e.target.closest('[data-civ]') : null;
            const which = hit ? hit.dataset.civ : (e.target === overlay ? 'close' : '');
            if (!which) return;
            document.removeEventListener('keydown', onKey);
            close();
            if (which === 'back' && opts && isPlain(opts.onBack) && typeof opts.onBack.run === 'function') {
                try { opts.onBack.run(); } catch (_) { /* the caller's problem, not the modal's */ }
            }
            if (which === 'apply' && opts && typeof opts.onApply === 'function') {
                try { opts.onApply(hit.dataset.civId || '', hit.dataset.civKind || ''); } catch (_) { /* the caller's problem, not the modal's */ }
            }
        });
        document.body.appendChild(overlay);
        return overlay;
    }

    function setBody(html) {
        const box = document.getElementById('civViewBody');
        if (box) box.innerHTML = html;
    }

    function titleFor(link) {
        return PROGRESS.includes(String((link && link.status) || '')) ? 'Where the client is so far' : 'What the client sent';
    }

    function setTitle(text) {
        const h = document.getElementById('civViewTitle');
        if (h) h.textContent = text;
    }

    // What has to change for a repaint to be worth it. A repaint under an
    // agent who is reading is the thing to avoid, so an unchanged poll leaves
    // the screen alone.
    function signature(link) {
        return [link.status, link.saveCount, link.lastOpenedAt, link.submittedAt, link.activeSeconds].join('|');
    }

    async function fetchLink(linkId) {
        const res = await Auth.apiFetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'get', id: linkId }),
        });
        let json = null;
        try { json = await res.json(); } catch (_) { json = null; }
        return { res, json };
    }

    /**
     * While a progress view is open, ask again every PROGRESS_POLL_MS and
     * repaint only when the client did something. A hidden tab skips its turn,
     * a failed poll keeps what is on screen, and once the form is submitted
     * the view becomes the ordinary answers view and stops asking.
     */
    function startPoll(linkId, sig) {
        stopPoll();
        let last = sig;
        let busy = false;
        _pollTimer = setInterval(async () => {
            if (busy || _openId !== linkId || !document.getElementById(MODAL_ID)) { if (_openId !== linkId) stopPoll(); return; }
            if (document.visibilityState === 'hidden') return;
            busy = true;
            try {
                const { res, json } = await fetchLink(linkId);
                if (_openId !== linkId || !res.ok || !json || json.ok === false || !json.link) return;
                const next = signature(json.link);
                if (next === last) return;
                last = next;
                setTitle(titleFor(json.link));
                setBody(renderBody(json.link));
                if (!PROGRESS.includes(String(json.link.status || ''))) stopPoll();
            } catch (_) {
                /* keep what is on screen */
            } finally {
                busy = false;
            }
        }, PROGRESS_POLL_MS);
    }

    /**
     * Fetch one submission and show it. Takes an id, not a link object,
     * because all three callers hold only an id — the `list` summary carries
     * no spec and no answers, so somebody has to make this call and one site
     * means one error sentence.
     */
    async function open(id, opts) {
        const linkId = String(id || '');
        if (!linkId) return false;
        // AFTER shell(), not before: shell() closes any modal already up, and
        // close() clears _openId. Claiming the id first meant the stale-response
        // guard below fired against the very request that set it.
        shell(note('Loading…'), opts);
        _openId = linkId;
        if (typeof Auth === 'undefined' || typeof Auth.apiFetch !== 'function') {
            setBody(note('Sign in to read client forms.'));
            return false;
        }
        let res;
        let json = null;
        try {
            ({ res, json } = await fetchLink(linkId));
        } catch (_) {
            setBody(note('Could not reach the server.'));
            return false;
        }
        // A second open() while this one was in flight owns the modal now.
        if (_openId !== linkId) return false;
        if (!res.ok || !json || json.ok === false || !json.link) {
            setBody(note((json && json.error) || `The server answered ${res.status}.`));
            return false;
        }
        setTitle(titleFor(json.link));
        setBody(renderBody(json.link));
        if (PROGRESS.includes(String(json.link.status || ''))) startPoll(linkId, signature(json.link));
        // A submitted form, or an applied one whose answers are still held
        // (the sweep purges them a few days after apply), can be applied:
        // again means a new intake, or the same draft once more.
        const slot = document.getElementById('civApplySlot');
        const st = String(json.link.status ?? '');
        const reusable = st === 'submitted' || (st === 'applied' && isPlain(json.link.answers));
        if (slot && opts && typeof opts.onApply === 'function' && reusable) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'ch-btn ch-btn-primary';
            btn.dataset.civ = 'apply';
            btn.dataset.civId = linkId;
            btn.dataset.civKind = String(json.link.kind || '');
            btn.textContent = st === 'applied' ? 'Use answers again' : 'Apply answers';
            slot.replaceChildren(btn);
        }
        return true;
    }

    return { open, close, renderBody, progressLine, timeLine, statusLabel, senderLabel, rowRefLabel, maskSsn, STATUS_LABEL, PROGRESS };
})();
